/**
 * @fileoverview Modal de criar/editar aula de um módulo da Academy (super admin).
 * Campo único "URL do vídeo" — o backend faz o parse (provider/videoId/videoHash)
 * e devolve ERR_ACADEMY_INVALID_VIDEO_URL se inválida (surfaceado via toastError).
 * Na edição, deixar a URL em branco mantém o vídeo atual. Estilo alinhado aos
 * modais TagModal/Transfer.
 */

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { X, Plus, Pencil } from "lucide-react";

import { i18n } from "../../translate/i18n";
import useAcademy from "../../hooks/useAcademy";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => {
  const dark = theme.palette.type === "dark";
  return {
    dialog: {
      "& .MuiDialog-paper": {
        fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        width: "min(520px, calc(100vw - 32px))",
        maxWidth: "calc(100vw - 32px)",
        borderRadius: 24,
        overflow: "hidden",
        background: dark
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
        boxShadow: dark
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
        border: dark
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      },
      "& .MuiBackdrop-root": {
        backgroundColor: dark ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.4)",
      },
    },
    modalHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2.5, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    },
    modalTitle: {
      fontSize: "1.35rem",
      fontWeight: 700,
      color: dark ? "#f8fafc" : "#111827",
    },
    modalClose: { color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280" },
    body: {
      padding: theme.spacing(3),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
        border: dark
          ? "1px solid rgba(91, 124, 255, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "& fieldset": { borderColor: "transparent" },
        "&:hover": {
          background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
          border: dark
            ? "1px solid rgba(91, 124, 255, 0.25)"
            : "1px solid rgba(61, 92, 253, 0.15)",
        },
        "&.Mui-focused": {
          background: dark ? "rgba(255, 255, 255, 0.08)" : "#ffffff",
          border: dark
            ? "1px solid rgba(91, 124, 255, 0.4)"
            : "1px solid rgba(61, 92, 253, 0.3)",
          boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)",
        },
      },
    },
    hint: {
      fontSize: "0.78rem",
      color: dark ? "rgba(248,250,252,0.5)" : "#9ca3af",
    },
    dialogActions: {
      padding: theme.spacing(2, 3),
      borderTop: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    cancelLeft: { marginRight: "auto" },
    btnBase: {
      height: 38,
      padding: "0 16px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      whiteSpace: "nowrap",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      boxShadow: "none",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      "& svg": { flexShrink: 0 },
      "& .MuiButton-label": { whiteSpace: "nowrap" },
    },
    btnCancel: {
      background: "transparent",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
      "&:hover": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
        color: dark ? "#f8fafc" : "#111827",
      },
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover": { background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)" },
      "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
    },
    spinner: { color: "#ffffff" },
  };
});

const AcademyLessonModal = ({ open, onClose, lesson, moduleId, reload }) => {
  const classes = useStyles();
  const { saveLesson, updateLesson } = useAcademy();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(lesson?.title || "");
    setDescription(lesson?.description || "");
    setVideoUrl("");
  }, [open, lesson]);

  const handleSave = async () => {
    if (!title || title.trim().length < 2) {
      toast.error(i18n.t("academy.manage.validation.titleRequired"));
      return;
    }
    if (!lesson?.id && !videoUrl.trim()) {
      toast.error(i18n.t("academy.manage.validation.videoUrlRequired"));
      return;
    }
    setSaving(true);
    try {
      if (lesson?.id) {
        await updateLesson(lesson.id, {
          title: title.trim(),
          description,
          ...(videoUrl.trim() ? { videoUrl: videoUrl.trim() } : {})
        });
      } else {
        await saveLesson({
          title: title.trim(),
          description,
          videoUrl: videoUrl.trim(),
          moduleId
        });
      }
      toast.success(i18n.t("academy.manage.toasts.lessonSaved"));
      if (typeof reload === "function") reload();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth={false}>
      <div className={classes.modalHeader}>
        <Typography className={classes.modalTitle}>
          {lesson?.id
            ? i18n.t("academy.manage.lessonModal.editTitle")
            : i18n.t("academy.manage.lessonModal.addTitle")}
        </Typography>
        <IconButton size="small" onClick={onClose} className={classes.modalClose}>
          <X size={18} strokeWidth={1.75} />
        </IconButton>
      </div>

      <DialogContent className={classes.body}>
        <TextField
          label={i18n.t("academy.manage.lessonModal.title")}
          value={title}
          onChange={e => setTitle(e.target.value)}
          variant="outlined"
          fullWidth
          autoFocus
          className={classes.textField}
        />
        <TextField
          label={i18n.t("academy.manage.lessonModal.description")}
          value={description}
          onChange={e => setDescription(e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          className={classes.textField}
        />
        <TextField
          label={i18n.t("academy.manage.lessonModal.videoUrl")}
          placeholder="https://youtu.be/... | https://vimeo.com/..."
          value={videoUrl}
          onChange={e => setVideoUrl(e.target.value)}
          variant="outlined"
          fullWidth
          className={classes.textField}
        />
        {lesson?.id && (
          <Typography className={classes.hint}>
            {i18n.t("academy.manage.lessonModal.currentVideo")}: {lesson.provider}/
            {lesson.videoId} — {i18n.t("academy.manage.lessonModal.keepHint")}
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          disabled={saving}
          className={`${classes.btnBase} ${classes.btnCancel} ${classes.cancelLeft}`}
          startIcon={<X size={16} strokeWidth={1.75} />}
        >
          {i18n.t("academy.manage.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className={`${classes.btnBase} ${classes.btnPrimary}`}
          startIcon={
            saving ? (
              <CircularProgress size={14} className={classes.spinner} />
            ) : lesson?.id ? (
              <Pencil size={16} strokeWidth={1.75} />
            ) : (
              <Plus size={16} strokeWidth={1.75} />
            )
          }
        >
          {i18n.t("academy.manage.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcademyLessonModal;
